/**
 * ============================================================
 * MONTE HERMOM | AUTH SAAS CONTEXT
 * ============================================================
 * ARQUIVO: /src/context/AuthSaaSContext.jsx
 * VERSÃO:  1.2.0
 * DATA:    16/09/2026
 *
 * MUDANÇA v1.2.0:
 *   - Passa a usar /api/auth/session.php (endpoint único)
 *   - Alinhado com o contrato: { logado, permitido, usuario }
 *   - check_session.php não é mais usado
 * ============================================================
 */

import React, { createContext, useContext, useEffect, useState } from 'react';

const AuthSaaSContext = createContext(null);

// ------------------------------------------------------------
// PROVIDER
// ------------------------------------------------------------
export function AuthSaaSProvider({ children }) {
  const [usuario, setUsuario] = useState(null);
  const [autenticado, setAutenticado] = useState(false);
  const [loading, setLoading] = useState(true);

  /**
   * Valida sessão ativa no backend
   * Endpoint único: /api/auth/session.php
   */
  const validarSessao = async () => {
    try {
      const response = await fetch('/api/auth/session.php', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });

      // 401 = sem sessão, 403 = sem tenant — ambos deslogam
      if (!response.ok) {
        setUsuario(null);
        setAutenticado(false);
        return;
      }

      const data = await response.json();

      if (data?.logado && data?.permitido) {
        setUsuario(data.usuario);
        setAutenticado(true);
      } else {
        setUsuario(null);
        setAutenticado(false);
      }
    } catch (error) {
      console.error('[AuthSaaS] Erro ao validar sessão:', error);
      setUsuario(null);
      setAutenticado(false);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout centralizado
   */
  const logout = async () => {
    try {
      await fetch('/api/auth/logout.php', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (_) {
      // silencioso
    } finally {
      setUsuario(null);
      setAutenticado(false);
    }
  };

  // ----------------------------------------------------------
  // BOOTSTRAP DA SESSÃO (1x por load)
  // ----------------------------------------------------------
  useEffect(() => {
    validarSessao();
  }, []);

  return (
    <AuthSaaSContext.Provider
      value={{
        usuario,
        autenticado,
        loading,
        validarSessao,
        logout
      }}
    >
      {children}
    </AuthSaaSContext.Provider>
  );
}

// ------------------------------------------------------------
// HOOK
// ------------------------------------------------------------
export function useAuthSaaS() {
  const context = useContext(AuthSaaSContext);

  if (!context) {
    throw new Error('useAuthSaaS deve ser usado dentro de AuthSaaSProvider');
  }

  return context;
}